import { Image } from 'expo-image';
import { Text, YStack } from 'tamagui';

import { CARD_HEIGHT } from '@/components/HomeSkeleton';
import PSLinkButton from '@/components/ui/PSLinkButton';
import {
  NETWORK_MESSAGE,
  SERVER_MESSAGE,
  UNKNOWN_MESSAGE,
  type ApiErrorKind,
} from '@/src/api/apiError';
import { color } from '@/constants/tokens';

/**
 * PSHomeSectionError — what a home section draws when `/home` fails (molecule).
 *
 * Stands in for a section's cards: the copy for the failure, an inline "retry",
 * and for the one failure the reader can act on, the no-internet cat. It holds a
 * carousel card's worth of height so the page keeps the footprint the cards
 * would have had and doesn't collapse around the failure.
 *
 * ONE `/home` call backs every section, so when this shows it shows in all of
 * them at once, and any one of them retries the same fetch.
 *
 * ── What it owns, and what the screen owns ──────────────────────────────
 * It takes the `kind`, not the words. The screen already knows which failure
 * happened; making it also look up the sentence would mean every call site
 * repeating the same map — and home renders four of these. So the wording per
 * kind lives here, the way `SecretMenuCard` owns the caption for each of its
 * states while the screen picks the state.
 *
 * `withArt` is the screen's call, because it's about the PAGE, not the failure:
 * the cat belongs in one section only (four cats down one page is the same joke
 * four times). Which failure deserves art is this component's call — network,
 * and only network, since the others aren't the reader's to fix.
 *
 * `onDark` is for the sections inside the Access Pass block: RN text doesn't
 * cascade, so the copy colour is set per call site rather than inherited from
 * the dark surface. The link stays brand purple either way — it's the accent
 * both surfaces already use.
 */

// The no-internet cat. Height follows the source's own 1408×768 ratio, so the
// art is never stretched — change the width and the height keeps up.
const ART_WIDTH = 300;
const ART_RATIO = 1408 / 768;

// Each string is the sentence UP TO the trailing "retry", which the link below
// finishes — so no kind can ship without the affordance.
//
// Only the network case gets its own words, because it's the only failure the
// user can act on: it names the thing to fix instead of apologising. The other
// three are the app's fault as far as the reader is concerned, and saying so
// three different ways would be a distinction without a difference.
//
// Typed `Record<ApiErrorKind, string>` so a fifth kind added to apiError.ts
// won't compile until someone decides what home says about it.

export default function PSHomeSectionError({
  kind,
  onRetry,
  isBackgroundDark = false,
}: {
  kind: ApiErrorKind;
  onRetry: () => void;
  isBackgroundDark?: boolean;
  /** Allow the art in this section — it still only draws for a network failure. */
}) {
  let copy = UNKNOWN_MESSAGE;
  if (kind === 'server') copy = SERVER_MESSAGE;
  if (kind === 'network') copy = NETWORK_MESSAGE;

  return (
    // A minimum, not a fixed height: the section can't collapse below a card,
    // and grows rather than clipping when the art is in it.
    <YStack minHeight={CARD_HEIGHT} justify="center" gap="$lg">
      <Image
        source={require('@/assets/images/no-internet-cat.png')}
        style={{
          width: ART_WIDTH,
          height: ART_WIDTH / ART_RATIO,
          alignSelf: 'center',
        }}
        contentFit="contain"
        transition={200}
        tintColor={isBackgroundDark ? color.borderColor : color.brand}
      />
      {/* "retry" is a PSLinkButton NESTED IN the copy rather than a control
          beside it — the app's pattern for a link inside a sentence (see the
          support paragraph in `app/bookings/[id]`); RN lays nested pressable
          Text out inline. */}
      <YStack alignItems="center" gap={'$space.sm'}>
        <Text
          fontFamily="$body"
          fontSize="$base"
          color={isBackgroundDark ? '$textWhite' : '$textDark'}
          textAlign="center"
          mx={'$space.xxl'}
        >
          {copy}{' '}
        </Text>

        <PSLinkButton title="Retry" onPress={onRetry} />
      </YStack>
    </YStack>
  );
}
